import { OptInAltinn3Banner } from '@Component/Banner/OptInAltinn3Banner.tsx';
import { Page } from '@Component/Page/Page';
import { runTour } from '@Hooks/useTourMe.ts';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext.ts';
import { InsufficientAccessPage } from '@Page/InsufficientAccessPage/InsufficientAccessPage.tsx';
import { PageHeaderActionsOverview } from '@Page/Overview/ActionButtons';
import { OverviewTable } from '@Page/Overview/OverviewTable';
import { useOverviewTableFilters } from '@Page/Overview/hooks/useSalaryOverviewTableFilters.tsx';
import SalaryOverviewTour from '@TourMe/SalaryOverviewTour.json';
import SalaryOverviewTourFiltersOverflow from '@TourMe/SalaryOverviewTourFiltersOverflow.json';
import { dateUtil } from '@Util/date.ts';
import { ErrorBoundary } from '@tlx/sentry';
import { getContextId, InlineMarkdown } from '@tlx/astro-shared';
import { FileInfo, PdfPageLayout, usePdfActions } from '@tlx/krr-pdf';
import { Tour } from '@tlx/tourme-core';
import { HalfTaxBanner } from '@tlx/tsk-shared';
import { useNavigate } from 'react-router';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import {
    Banner,
    BannerActions,
    BannerCloseButton,
    BannerContent,
    BannerDescription,
    BannerHeader,
    BannerImage,
    Button,
} from '@tlx/atlas';
import { previewPayslipsUrl, previewVoucherUrl, viewVoucherUrl } from '@Util/links.ts';
import useLocalStorage from '@Hooks/storage.ts';
import { useSalarySettings } from '@Page/Onboarding/hooks/useSalarySettings.ts';

// a period in this case it is something like date,date,Year in Salary Overview
export const getToYearFromPeriod = (period: string) => {
    return dateUtil.parseDate(period.split(',')[1])?.getFullYear() ?? new Date().getFullYear();
};

export function Overview() {
    const navigator = useNavigate();
    const intl = useIntl();
    const { authLevelWage, isLoading } = useTskContext();

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const filters = useOverviewTableFilters();

    const { doOpenPdf, setVoucher, setVoucherAndFiles, setFiles } = usePdfActions();

    const moduleSalaryApproval = useSalarySettings().data?.moduleSalaryApproval;

    const [salaryApprovalBannerInfo, setSalaryApprovalBannerInfo] = useLocalStorage(
        'salary_approval_banner_info',
        true,
    );

    function runSalaryOverviewTour() {
        //Showing a different tour if the quick filters don't have space on the screen

        const filterOverflowing = document
            .querySelector("[data-testid='filter-bar']")
            ?.classList.contains('atl-filter-bar');
        if (filterOverflowing) {
            runTour(SalaryOverviewTourFiltersOverflow as Tour, {
                testMode: true,
                language: intl.locale,
            });
            return;
        }
        runTour(SalaryOverviewTour as Tour, {
            testMode: true,
            language: intl.locale,
        });
    }

    if (isLoading) {
        return null;
    } else if (authLevelWage < AUTH_LEVEL_DEPARTMENT) {
        return <InsufficientAccessPage />;
    }

    function handleSetSelectedPdfItem(selectedPdfItem: {
        voucherId: number;
        transactionId: number;
        isPayslip: boolean;
    }) {
        const pdfVoucherData = {
            url:
                selectedPdfItem.voucherId === 0
                    ? previewVoucherUrl(selectedPdfItem.transactionId)
                    : viewVoucherUrl(selectedPdfItem.voucherId),
            voucherId: selectedPdfItem.voucherId,
            transactionId: selectedPdfItem.transactionId,
            title: intl.formatMessage({ id: 'text_voucher' }),
        };

        const pdfPayslipsData = {
            url: previewPayslipsUrl(selectedPdfItem.transactionId),
            voucherId: selectedPdfItem.voucherId,
            transactionId: selectedPdfItem.transactionId,
            title: intl.formatMessage({ id: 'text_payslip' }),
        };

        if (moduleSalaryApproval) {
            // If salary approval is enabled, we check if both voucher/preview voucher and payslips data are available
            if (pdfVoucherData.url && pdfPayslipsData.url) {
                const voucherFile = getVoucherFile(pdfVoucherData);
                const payslipsFile = getPayslipsFile(pdfPayslipsData);
                const files: FileInfo[] = [...voucherFile, ...payslipsFile];

                setVoucher(
                    Number(getContextId() ?? undefined),
                    pdfVoucherData.voucherId === 0 ? undefined : pdfVoucherData.voucherId,
                );
                // Set the voucher file and payslips files and preselect the payslips one based on the isPayslip flag
                // If isPayslip is true, then we preselect the payslips file (the second one in the array)
                // If isPayslip is false, then we preselect the voucher file (the first one in the array)
                setFiles(files, false, selectedPdfItem.isPayslip ? (files?.[1].id ?? 0) : (files?.[0].id ?? 0));
                doOpenPdf();
            }
        } else if (pdfVoucherData.url) {
            // If salary approval is not enabled, we only set the voucher file
            // and clear the title of the file to avoid displaying it in the PDF viewer as a chip
            pdfVoucherData.title = '';
            const voucherFile = getVoucherFile(pdfVoucherData);

            setVoucherAndFiles(
                Number(getContextId() ?? undefined),
                pdfVoucherData.voucherId === 0 ? undefined : pdfVoucherData.voucherId,
                voucherFile,
            );
            doOpenPdf();
        }
    }

    const year = getToYearFromPeriod(filters.periodFilter.query);

    return (
        <PdfPageLayout navigate={navigator}>
            <Page className={'salary-overview__page'}>
                <PageHeaderActionsOverview runSalaryOverviewTour={runSalaryOverviewTour} year={year} />
                <OptInAltinn3Banner />
                <ErrorBoundary>
                    <div className="atl-mt-24">
                        <HalfTaxBanner />
                    </div>
                </ErrorBoundary>
                {/* TRIP-60608: temporarily disabled while the Altinn-3 opt-in banner is shown. Restore when that banner is removed. - Agent */}
                {/* eslint-disable-next-line no-constant-binary-expression */}
                {false && moduleSalaryApproval && salaryApprovalBannerInfo && (
                    <Banner>
                        <BannerImage className="tsk-object-fit-fill" src="gifs/visningsvalg-banner.gif" alt="" />
                        <BannerCloseButton
                            onClick={() => {
                                setSalaryApprovalBannerInfo(false);
                            }}
                        />
                        <BannerContent>
                            <BannerHeader>
                                {intl.formatMessage({
                                    id: 'tsk_salary_approval_banner_title',
                                })}
                            </BannerHeader>
                            <BannerDescription>
                                <span>
                                    <InlineMarkdown>
                                        {intl.formatMessage({
                                            id: 'tsk_salary_approval_banner_info',
                                        })}
                                    </InlineMarkdown>
                                </span>
                            </BannerDescription>
                            <BannerActions>
                                <Button
                                    variant="primary"
                                    data-testid="primary-read-more-button"
                                    onClick={() => {
                                        window.open(
                                            'https://hjelp.tripletex.no/hc/no/articles/36803502384145-Slik-godkjenner-du-l%C3%B8nnslipper',
                                            '_blank',
                                        );
                                    }}
                                    aria-label="primary"
                                >
                                    {intl.formatMessage({
                                        id: 'text_read_more',
                                    })}
                                </Button>
                                <Button
                                    variant="secondary"
                                    data-testid="seccondary-button"
                                    onClick={() => {
                                        setSalaryApprovalBannerInfo(false);
                                    }}
                                    aria-label="secondary"
                                >
                                    {intl.formatMessage({
                                        id: 'button_close',
                                    })}
                                </Button>
                            </BannerActions>
                        </BannerContent>
                    </Banner>
                )}
                <OverviewTable
                    scrollableContainerRef={scrollContainerRef}
                    filterState={filters}
                    setSelectedPdfItem={handleSetSelectedPdfItem}
                />
            </Page>
        </PdfPageLayout>
    );
}

function getVoucherFile(fileData: {
    url: string;
    voucherId: number;
    title: string;
    transactionId: number;
}): FileInfo[] {
    return [
        {
            id:
                'voucher-' +
                (fileData.voucherId !== 0
                    ? fileData.voucherId // Use voucherId if it's not 0 to ensure uniqueness
                    : fileData.transactionId), // Fallback ID if voucherId is 0
            isVoucher: fileData.voucherId !== 0,
            url: fileData.url,
            title: fileData.title,
            mimeType: 'application/pdf',
        },
    ];
}

function getPayslipsFile(fileData: { url: string; transactionId: number; title: string }): FileInfo[] {
    return [
        {
            id: 'payslips-' + fileData.transactionId, // ID based on transactionId
            isVoucher: false,
            url: fileData.url,
            title: fileData.title,
            mimeType: 'application/pdf',
        },
    ];
}

const redirectUrl = () => {
    const contextId = getContextId();
    return '/execute/wageTransactions?contextId=' + contextId;
};

Overview.redirectUrl = redirectUrl;
